import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonLoader from '../../../Components/CommonLoader';
import CommonSearchBar from '../../../Components/CommonSearchBar';
import CommonTable from '../../../Components/CommonTable';
import CommonActionMenu from '../../../Components/CommonActionMenu';
import StatusBadge from '../../../Components/StatusBadge';
import NoDataPage from '../../../Components/NoDataPage';
import { useToast } from '../../../Components/CommonToast';
import { useConfirm } from '../../../Components/ConfirmModal';
import { heading3, bodySmall, fontSize, Body } from '../../../Constants/Fonts';
import AddOnCard from '../../../Dashboard/AddOns/AddOnCard';
import {
    risk_portal_projects_list,
    risk_portal_project_breakdown,
    risk_portal_project_delete,
} from '../../../services';
import ProjectForm from '../Forms/ProjectForm';
import PageHeading from '../../../Components/PageHeading';

const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

const StatValue = ({ value, color }) => (
    <Body as="span" color={color || 'var(--color-text-primary)'} weight="bold" style={{ fontSize: fontSize['3xl'] }}>{value}</Body>
);

const HEADERS = ['Name', 'Code', 'Type', 'Business Unit', 'Manager', 'Location', 'Risks', 'By Severity', 'Status', 'Actions'];

// Compact severity chips — one per tier (C/H/M/L). Tinted with the severity's
// soft token when the count is non-zero, muted when zero, so a project's risk
// mix reads at a glance instead of a cramped "0 crit / 0 high / …" string.
const SEV = [
    { key: 'critical', short: 'C', token: 'critical' },
    { key: 'high', short: 'H', token: 'high' },
    { key: 'medium', short: 'M', token: 'medium' },
    { key: 'low', short: 'L', token: 'low' },
];

function SeverityChips({ counts }) {
    return (
        <Body as="span" style={{ display: 'inline-flex', gap: 6, whiteSpace: 'nowrap' }}>
            {SEV.map((s) => {
                const n = counts[s.key] || 0;
                const on = n > 0;
                return (
                    <Body
                        as="span"
                        variant={2}
                        weight="semibold"
                        key={s.key}
                        title={`${n} ${s.key}`}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            minWidth: 34, justifyContent: 'center',
                            padding: '2px 8px', borderRadius: 'var(--radius-pill)',
                            color: on ? `var(--color-${s.token})` : 'var(--color-text-muted)',
                            background: on ? `var(--color-${s.token}-soft)` : 'var(--color-surface-muted)',
                        }}
                    >
                        <Body as="span" style={{ opacity: 0.7 }}>{s.short}</Body>{n}
                    </Body>
                );
            })}
        </Body>
    );
}

export default function Projects({ role = 'risk_admin', businessUnits = [] }) {
    const [projects, setProjects] = useState([]);
    const [breakdownById, setBreakdownById] = useState({});
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [query, setQuery] = useState('');

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();
    const canManage = role === 'risk_admin' || role === 'risk_manager';
    const buMap = Object.fromEntries(businessUnits.map((b) => [b.id, b]));

    const buNameOf = useCallback(
        (p) => p.business_unit || (p.business_unit_id ? (buMap[p.business_unit_id]?.name || '') : ''),
        [buMap],
    );

    // Portfolio roll-up across every project's severity breakdown — drives the
    // summary tiles (total risks / attention-needed) regardless of the filter.
    const summary = useMemo(() => {
        const totals = Object.values(breakdownById).reduce(
            (acc, b) => {
                const c = b?.counts || {};
                acc.total += c.total || 0;
                acc.attention += (c.critical || 0) + (c.high || 0);
                return acc;
            },
            { total: 0, attention: 0 },
        );
        return {
            projects: projects.length,
            active: projects.filter((p) => p.is_active !== false).length,
            risks: totals.total,
            attention: totals.attention,
        };
    }, [projects, breakdownById]);

    // Client-side filter across the fields a user is likely to scan for.
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return projects;
        return projects.filter((p) =>
            [p.name, p.code, p.project_type, p.project_manager, p.location, buNameOf(p)]
                .some((v) => String(v || '').toLowerCase().includes(q)),
        );
    }, [projects, query, buNameOf]);

    const load = useCallback(async () => {
        try {
            const [listRes, bdRes] = await Promise.allSettled([
                risk_portal_projects_list(),
                risk_portal_project_breakdown(),
            ]);
            const listD = listRes.status === 'fulfilled' ? unwrap(listRes.value) : null;
            const bd = bdRes.status === 'fulfilled' ? unwrap(bdRes.value) : null;
            setProjects(Array.isArray(listD?.projects) ? listD.projects : Array.isArray(listD) ? listD : []);
            setBreakdownById(Object.fromEntries((bd?.projects || []).map((p) => [p.id, p])));
        } catch (e) {
            notify('Failed to load projects', 'error');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    const op = async (fn, okMsg) => {
        setBusy(true);
        try {
            await fn();
            if (okMsg) notify(okMsg, 'success');
            await load();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Action failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const openAdd = () => { setEditing(null); setShowForm(true); };
    const openEdit = (p) => { setEditing(p); setShowForm(true); };
    const remove = async (p) => {
        const ok = await confirm({
            title: 'Delete project',
            message: `Delete "${p.name}"? This can't be undone if no risks reference it.`,
            confirmLabel: 'Delete', danger: true, themeColor: false,
        });
        if (!ok) return;
        op(() => risk_portal_project_delete(p.id), `Project "${p.name}" deleted`);
    };

    const rows = filtered.map((p) => {
        const counts = breakdownById[p.id]?.counts || { total: 0, critical: 0, high: 0, medium: 0, low: 0 };
        const buName = p.business_unit || (p.business_unit_id ? (buMap[p.business_unit_id]?.name || '—') : '—');
        const active = p.is_active !== false;
        return {
            data: [
                <Body as="span" color="var(--color-text-primary)" weight="semibold" style={{ opacity: active ? 1 : 0.55 }}>{p.name}</Body>,
                <Body as="span" variant={2} color="var(--color-text-muted)">{p.code || '—'}</Body>,
                <Body as="span" variant={2} color="var(--color-text-secondary)" style={{ textTransform: 'capitalize' }}>{p.project_type || '—'}</Body>,
                <Body as="span" variant={2} color="var(--color-text-secondary)">{buName}</Body>,
                <Body as="span" variant={2} color="var(--color-text-secondary)">{p.project_manager || '—'}</Body>,
                <Body as="span" variant={2} color="var(--color-text-secondary)">{p.location || '—'}</Body>,
                <Body as="span" variant={2} color="var(--color-text-primary)" style={{ fontVariantNumeric: 'tabular-nums' }}><strong>{counts.total || 0}</strong></Body>,
                <SeverityChips counts={counts} />,
                <StatusBadge status={active ? 'active' : 'inactive'} />,
                canManage ? (
                    <CommonActionMenu
                        actions={[
                            { label: 'Edit', onClick: () => openEdit(p) },
                            { label: 'Delete', onClick: () => remove(p), danger: true },
                        ]}
                    />
                ) : <Body as="span" variant={2} color="var(--color-text-muted)">—</Body>,
            ],
        };
    });

    if (loading) return <CommonLoader fullPage label="Loading projects…" />;

    return (
        <div className="rp1_widget">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                <PageHeading
                    title='Projects'
                    subtitle=" Projects let you slice risks by initiative, programme, or product. Each project belongs to a Business Unit; risks then carry both BU and Project for executive reporting."
                />
                {canManage && <CommonButton text="Add Project" onClick={openAdd} />}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, margin: '14px 0' }}>
                <AddOnCard  icon="layers" label="Projects" action={<StatValue value={summary.projects} />} />
                <AddOnCard icon="allowlist" label="Active" action={<StatValue value={summary.active} />} />
                <AddOnCard icon="risk" label="Total Risks" action={<StatValue value={summary.risks} />} />
                <AddOnCard icon="violation" label="Critical + High" action={<StatValue value={summary.attention} color={summary.attention ? 'var(--color-high)' : undefined} />} />
            </div>

            {projects.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '0 0 12px' }}>
                    <CommonSearchBar
                        value={query}
                        onChange={setQuery}
                        placeholder="Search projects by name, code, BU, manager…"
                        ariaLabel="Search projects"
                        style={{ maxWidth: 340 }}
                    />
                    <Body as="span" variant={2} color="var(--color-text-muted)" style={{ whiteSpace: 'nowrap' }}>
                        {filtered.length} of {projects.length}
                    </Body>
                </div>
            )}

            {filtered.length === 0 ? (
                <NoDataPage hint={query.trim() ? `No projects match "${query.trim()}".` : 'No projects yet. Click Add Project to create the first.'} />
            ) : (
                <CommonTable headers={HEADERS} rows={rows} noPadding noScroll />
            )}

            {showForm && (
                <ProjectForm
                    key={editing?.id || 'new'}
                    show
                    editing={editing}
                    businessUnits={businessUnits}
                    onClose={() => setShowForm(false)}
                    onSuccess={(msg) => { notify(msg, 'success'); load(); }}
                    onError={(msg) => notify(msg, 'error')}
                />
            )}

            {busy && <CommonLoader fullPage label="Working…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}
